/**
 * Timezone utilities for consistent timestamp handling
 * Converts UTC timestamps from backend for display
 */

export const EDT_TIMEZONE = 'America/New_York';

/**
 * Format a UTC timestamp to EDT/EST time string
 * @param utcTimestamp - UTC timestamp from backend (string or Date)
 * @returns Formatted time string in EDT/EST (e.g., "12:37 AM")
 */
export function formatToEDT(utcTimestamp: string | Date): string {
  if (!utcTimestamp) return '';
  
  const date = typeof utcTimestamp === 'string' ? new Date(utcTimestamp) : utcTimestamp;
  
  // Handle invalid dates
  if (isNaN(date.getTime())) {
    console.warn('Invalid timestamp provided to formatToEDT:', utcTimestamp);
    return '';
  }
  
  return date.toLocaleTimeString(undefined, {
    hour: 'numeric',
    minute: '2-digit', 
    hour12: true,
    timeZone: EDT_TIMEZONE // Automatically handles EDT/EST based on date
  });
}

/**
 * Format naive UTC datetime string from VectorDB for EDT display
 * VectorDB returns timestamps like "2025-09-23T05:38:01.399270" which are UTC but missing timezone info
 * @param naiveUtcTimestamp - Naive UTC datetime string from VectorDB
 * @returns Formatted time string in EDT
 */
// Parse a VectorDB naive UTC timestamp (e.g., "2025-09-23T05:38:01.399270") as a Date in UTC
export function parseVectorDbUtc(naiveUtcTimestamp: string): Date | null {
  if (!naiveUtcTimestamp) return null;
  try {
    let utcString = naiveUtcTimestamp;
    // If there is no explicit timezone designator, append 'Z' (UTC)
    // Detect timezone designator: trailing 'Z' or [+/-]HH:MM
    const hasTimezone = /Z$|[+-]\d{2}:?\d{2}$/.test(naiveUtcTimestamp);
    if (!hasTimezone) {
      utcString = naiveUtcTimestamp + 'Z';
    }
    const d = new Date(utcString);
    if (isNaN(d.getTime())) return null;
    return d;
  } catch {
    return null;
  }
}

// Format VectorDB naive UTC timestamp in EDT timezone
export function formatVectorDBTimestamp(naiveUtcTimestamp: string): string {
  if (!naiveUtcTimestamp) return '';
  
  try {
    const utcDate = parseVectorDbUtc(naiveUtcTimestamp);
    if (!utcDate) {
      console.warn('Invalid timestamp from VectorDB:', naiveUtcTimestamp);
      return '';
    }
    // Convert to EDT for display (always use America/New_York)
    const edtTime = utcDate.toLocaleTimeString('en-US', {
      hour: 'numeric',
      minute: '2-digit',
      hour12: true,
      timeZone: EDT_TIMEZONE
    });

    // Debug logging in development
    if (import.meta.env.DEV) {
      console.log('VectorDB Timestamp Conversion:', {
        input: naiveUtcTimestamp,
        utcDate: utcDate.toUTCString(),
        edtTime: edtTime,
        timezone: EDT_TIMEZONE
      });
    }
    
    return edtTime;
    
  } catch (error) {
    console.warn('Failed to parse VectorDB timestamp:', naiveUtcTimestamp, error);
    return '';
  }
}

/**
 * Format VectorDB naive UTC timestamp with date and time for message display
 * @param naiveUtcTimestamp - Naive UTC datetime string from VectorDB
 * @returns Formatted date and time string in EDT timezone (e.g., "Jan 15, 12:37 PM")
 */
export function formatVectorDBTimestampWithDate(naiveUtcTimestamp: string): string {
  if (!naiveUtcTimestamp) return '';
  
  try {
    const utcDate = parseVectorDbUtc(naiveUtcTimestamp);
    if (!utcDate) {
      console.warn('Invalid timestamp from VectorDB:', naiveUtcTimestamp);
      return '';
    }
    
    // Format with full date and time in EDT timezone
    const edtDateTime = utcDate.toLocaleString('en-US', {
      weekday: 'short',
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
      second: '2-digit',
      hour12: true,
      timeZone: EDT_TIMEZONE
    });

    return edtDateTime;
    
  } catch (error) {
    console.warn('Failed to parse VectorDB timestamp:', naiveUtcTimestamp, error);
    return '';
  }
}

/**
 * Format a UTC timestamp to EDT/EST date and time string
 * @param utcTimestamp - UTC timestamp from backend (string or Date)
 * @returns Formatted date and time string in EDT/EST (e.g., "Jan 15, 12:37 AM")
 */
export function formatToEDTDateTime(utcTimestamp: string | Date): string {
  if (!utcTimestamp) return '';
  
  const date = typeof utcTimestamp === 'string' ? new Date(utcTimestamp) : utcTimestamp;
  
  if (isNaN(date.getTime())) {
    console.warn('Invalid timestamp provided to formatToEDTDateTime:', utcTimestamp);
    return '';
  }
  
  return date.toLocaleString(undefined, {
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
    timeZone: EDT_TIMEZONE
  });
}

/**
 * Get current time in EDT/EST
 * @returns Current Date object
 */
export function getCurrentEDT(): Date {
  return new Date(); // Browser automatically handles local timezone
}

/**
 * Check if we're currently in Daylight Time (EDT) or Standard Time (EST)
 * @returns true if EDT (spring/summer), false if EST (fall/winter)
 */
export function isCurrentlyEDT(): boolean {
  const now = new Date();
  const formatter = new Intl.DateTimeFormat('en-US', { 
    timeZone: EDT_TIMEZONE, 
    timeZoneName: 'short' 
  });
  
  const timeZoneName = formatter.formatToParts(now)
    .find(part => part.type === 'timeZoneName')?.value;
  
  return timeZoneName === 'EDT';
}

/**
 * Debug function to show timezone conversion
 * @param utcTimestamp - UTC timestamp to debug
 */
export function debugTimezoneConversion(utcTimestamp: string | Date): void {
  if (import.meta.env.DEV) {
    const date = typeof utcTimestamp === 'string' ? new Date(utcTimestamp) : utcTimestamp;
    console.log('Timezone Conversion Debug:', {
      original: utcTimestamp,
      utc: date.toUTCString(),
      edt: formatToEDT(date),
      edtDateTime: formatToEDTDateTime(date),
      isEDT: isCurrentlyEDT(),
      browserTimezone: Intl.DateTimeFormat().resolvedOptions().timeZone
    });
  }
}
